import axios from 'axios';

import { UserDto } from '@tallo/user';

import { EmailChangeStatusDto, SessionDto, TwoFactorSetupDto } from '../dtos/profile.dtos';

class ProfileService {
  async uploadAvatar(file: File): Promise<UserDto> {
    const formData = new FormData();
    formData.append('image', file);

    return (await axios.post<UserDto>('profile/avatar', formData)).data;
  }

  async removeAvatar(): Promise<void> {
    await axios.delete('profile/avatar');
  }

  async sendPhoneVerificationCode(): Promise<void> {
    await axios.post('profile/phone/send-verification-code');
  }

  async verifyPhone(code: string): Promise<UserDto> {
    return (await axios.post<UserDto>('profile/phone/verify', { code })).data;
  }

  async getEmailChange(): Promise<EmailChangeStatusDto> {
    return (await axios.get<EmailChangeStatusDto>('profile/email-change')).data;
  }

  async requestEmailChange(newEmail: string, password?: string): Promise<EmailChangeStatusDto> {
    return (
      await axios.post<EmailChangeStatusDto>('profile/email-change/request', {
        newEmail,
        password: password || undefined,
      })
    ).data;
  }

  async resendEmailChangeLink(): Promise<void> {
    await axios.post('profile/email-change/resend');
  }

  async cancelEmailChange(): Promise<void> {
    await axios.delete('profile/email-change');
  }

  /** Public endpoint - the link can land on a signed-out device */
  async confirmEmailChange(token: string): Promise<void> {
    await axios.post('profile/email-change/confirm', { token });
  }

  async changePassword(newPassword: string, currentPassword?: string): Promise<void> {
    await axios.patch('profile/password', { newPassword, currentPassword: currentPassword || undefined });
  }

  async setupTwoFactor(): Promise<TwoFactorSetupDto> {
    return (await axios.post<TwoFactorSetupDto>('profile/two-factor/setup')).data;
  }

  async enableTwoFactor(code: string): Promise<string[]> {
    return (await axios.post<{ backupCodes: string[] }>('profile/two-factor/enable', { code })).data.backupCodes;
  }

  async disableTwoFactor(code: string): Promise<void> {
    await axios.post('profile/two-factor/disable', { code });
  }

  async listSessions(): Promise<SessionDto[]> {
    return (await axios.get<SessionDto[]>('profile/sessions')).data;
  }

  async revokeSession(sessionId: string): Promise<void> {
    await axios.delete(`profile/sessions/${sessionId}`);
  }
}

export default new ProfileService();
